<template>
  <div>
    <legend>Rate Limit</legend>

    <CRow
      form
      class="form-group"
    >
      <CCol
        tag="label"
        sm="3"
        class="col-form-label text-right font-weight-bold"
      >
        {{ toggleLabel }}
      </CCol>
      <CCol
        sm="9"
        class="mt-1"
      >
        <CSwitch
          shape="pill"
          :checked.sync="enabled"
          color="primary"
          @update:checked="onToggle"
        />
      </CCol>
    </CRow>

    <CRow
      v-if="enabled"
      form
      class="form-group"
    >
      <CCol
        tag="label"
        sm="3"
        class="col-form-label text-right font-weight-bold"
      >
        Max Requests
      </CCol>
      <CCol
        sm="5"
        lg="4"
      >
        <CInput
          :value="maxCalls"
          type="number"
          :min="1"
          name="Max Requests"
          placeholder="Max Requests"
          :is-valid="validationState"
          invalid-feedback="Max Requests must be at least 1"
          @input="onMaxCallsInput"
        >
          <template #append>
            <CSelect
              custom
              class="mb-0"
              :value.sync="rateLimitPeriod"
              :options="periodOptions"
              @update:value="onPeriodChange"
            />
          </template>
        </CInput>
      </CCol>
    </CRow>
  </div>
</template>

<script>
const PERIOD_TO_MINUTES = { minutely: 1, hourly: 60, daily: 1440 }
const MINUTES_TO_PERIOD = { 1: 'minutely', 60: 'hourly', 1440: 'daily' }

export default {
  name: 'RateLimitInput',

  props: {
    value: {
      type: Object,
      default: null
    },
    submitted: {
      type: Boolean,
      default: false
    },
    toggleLabel: {
      type: String,
      default: 'Override Rate Limit'
    },
    defaultMaxCalls: {
      type: Number,
      default: 100
    },
    defaultPerMinutes: {
      type: Number,
      default: 1
    }
  },

  data() {
    return {
      enabled: false,
      maxCalls: null,
      rateLimitPeriod: 'minutely'
    }
  },

  computed: {
    periodOptions() {
      return [
        { value: 'minutely', label: 'Per Minute' },
        { value: 'hourly', label: 'Per Hour' },
        { value: 'daily', label: 'Per Day' }
      ]
    },

    isValid() {
      return !this.enabled || (typeof this.maxCalls === 'number' && this.maxCalls >= 1)
    },

    validationState() {
      if (!this.submitted || !this.enabled) {
        return null
      }

      return this.isValid
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.enabled = val != null
        if (val) {
          this.maxCalls = val.maxNumberOfCalls
          this.rateLimitPeriod = MINUTES_TO_PERIOD[val.perMinutes] ?? 'minutely'
        }
      }
    },

    isValid: {
      immediate: true,
      handler(val) {
        this.$emit('validity-change', val)
      }
    }
  },

  methods: {
    onToggle(newEnabled) {
      if (newEnabled) {
        this.maxCalls = this.defaultMaxCalls
        this.rateLimitPeriod = MINUTES_TO_PERIOD[this.defaultPerMinutes] ?? 'minutely'
        this.$emit('input', {
          maxNumberOfCalls: this.defaultMaxCalls,
          perMinutes: this.defaultPerMinutes
        })
      } else {
        this.maxCalls = null
        this.$emit('input', null)
      }
    },

    onMaxCallsInput(value) {
      this.maxCalls = value === '' ? null : Number(value)
      this.$emit('input', {
        maxNumberOfCalls: this.maxCalls,
        perMinutes: PERIOD_TO_MINUTES[this.rateLimitPeriod]
      })
    },

    onPeriodChange(value) {
      this.rateLimitPeriod = value
      this.$emit('input', {
        maxNumberOfCalls: this.maxCalls,
        perMinutes: PERIOD_TO_MINUTES[this.rateLimitPeriod]
      })
    }
  }
}
</script>
